import {
	useCallback,
	useEffect,
	useImperativeHandle,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
	type KeyboardEvent as ReactKeyboardEvent,
	type ClipboardEvent as ReactClipboardEvent,
	type ChangeEvent,
	type CSSProperties,
	type ReactNode,
	type Ref,
} from "react";
import { IconButton } from "@/components/common/IconButton";
import { RichTooltip } from "@/components/common/RichTooltip";
import { TOOLTIP_DEFAULT_DELAY } from "@/components/common/TooltipLayer";
import { ChatModelSelector } from "@/components/chat/ChatModelSelector";
import { ChatPermissionModeSelector } from "@/components/chat/ChatPermissionModeSelector";
import { ContextUsageMeter } from "@/components/chat/ContextUsageMeter";
import {
	selectLatestContextUsage,
	useChatStore,
	type ConnectionStatus,
} from "@/stores/useChatStore";
import { useLayoutStore } from "@/stores/useLayoutStore";
import { useChatDraftStore } from "@/stores/useChatDraftStore";
import { useTranscriptSearchStore } from "@/stores/useTranscriptSearchStore";
import { composerPrimaryAction, type ComposerSendBlocker } from "@/lib/composer-actions";
import { buildUserMessageHistory, resolveComposerArrow } from "@/lib/composer-history";
import { isImeConsumedKeyDown } from "@/lib/ime-composition";
import {
	historyNext,
	historyPrevious,
	INITIAL_HISTORY_NAV,
	type HistoryNavState,
} from "@/lib/input-history";
import { attachmentFromFile, type ComposerAttachment } from "@/lib/chat-attachments";
import { classifyAttachment } from "@/lib/document-attachments";
import { Icon } from "@/components/common/Icon";

/** Upper bound for the textarea auto-resize. Matches the historical maxHeight. */
const TEXTAREA_MAX_HEIGHT = 180;

/** 送信 / 停止ボタンの一辺 (px)。円形なので直径にあたる。 */
const PRIMARY_BUTTON_SIZE = 28;

/** 入力が空で送信できない間だけ、送信ボタンのホバーで出すキーボード操作のヒント。 */
const SEND_HINTS = (
	<span style={{ whiteSpace: "nowrap", fontFamily: "var(--font-mono)" }}>
		↑ で履歴
		<br />
		shift + ⏎ で改行
	</span>
);

/**
 * 送信できない理由ごとの、送信ボタンのホバー案内。理由と無関係な案内を出さないよう、
 * 伝えることがある理由だけ中身を返す。null の理由ではホバーしても何も出さない。
 * 遅延は案内の性質で決める。急がない操作の説明は他のツールチップと同じだけ待たせ、
 * 一瞬で解消する状態の案内は待つ間に消えてしまうため即座に出す。
 */
function sendHintFor(blocker: ComposerSendBlocker | null): { content: ReactNode; delayMs: number } {
	switch (blocker) {
		// 押せない理由が入力の不足だけなら、次に何をすれば良いかとして操作を案内する。
		case "empty":
			return { content: SEND_HINTS, delayMs: TOOLTIP_DEFAULT_DELAY };
		// pending は待つ以外に無く一瞬で解消する。blocked の理由は入力欄の placeholder が
		// 既に説明しているため、ここで繰り返さない。
		default:
			return { content: null, delayMs: 0 };
	}
}

/**
 * Reset the textarea to its natural height, measure the content, and clamp
 * the resulting height to maxHeight. Exported for unit tests; the live
 * component uses it inside a layout effect keyed on the text value.
 */
function resizeTextarea(el: HTMLTextAreaElement, maxHeight: number): void {
	// "auto" forces the browser to recompute scrollHeight against the natural
	// (unstyled) content size; without this step, a previously-set larger height
	// would keep scrollHeight stuck and the textarea could never shrink.
	el.style.height = "auto";
	el.style.height = `${Math.min(el.scrollHeight, maxHeight)}px`;
}

/** 添付チップで共通のベーススタイル。gap・padding・color・borderRadius は用途ごとに上書きする。 */
const CHIP_STYLE: CSSProperties = {
	display: "inline-flex",
	alignItems: "center",
	fontSize: "var(--text-caption)",
	background: "var(--surface-2)",
	boxShadow: "var(--shadow-ring)",
};

/** チップ内の削除ボタン共通スタイル。 */
const CHIP_REMOVE_BUTTON_STYLE: CSSProperties = {
	background: "transparent",
	border: 0,
	padding: 0,
	color: "var(--fg-3)",
	cursor: "pointer",
	lineHeight: "var(--leading-none)",
	flexShrink: 0,
};

interface ComposerInputProps {
	onSend: (text: string, attachments: ComposerAttachment[]) => Promise<void>;
	sendDisabled?: boolean;
	/** 入力自体を受け付けない (アーカイブ済みなど)。placeholder で理由を示す。 */
	inputDisabled?: boolean;
	placeholder?: string;
	/** 親が送信先チャット単位で管理する POST の解決待ち状態。 */
	sendPending?: boolean;
	/** エージェント実行状態。送信ボタンを送信 / 停止 / 停止中へ切り替える。 */
	status?: ConnectionStatus;
	/** 実行中(streaming)に送信ボタンが停止ボタンとして押されたときの処理。 */
	onStop?: () => void;
	ref?: Ref<ComposerInputHandle>;
}

/** 親 (CenterPane) から入力欄を操作するための ref ハンドル。 */
export interface ComposerInputHandle {
	/** 添付ボタン以外の経路 (CenterPane 全体への D&D) から届いたファイルを取り込む。 */
	addFiles: (files: File[]) => void;
	/** 履歴検索を閉じたあとなど、外から入力欄へフォーカスを戻す。 */
	focus: () => void;
}

const SLASH_COMMANDS: readonly { slug: string; label: string; hint: string }[] = [
	{ slug: "/segment", label: "/segment", hint: "動画を要素動作に分割" },
	{ slug: "/plan", label: "/plan", hint: "収集プランを生成" },
	{ slug: "/distribute", label: "/distribute", hint: "データ分布案を提示" },
	{ slug: "/review", label: "/review", hint: "現在の出力をレビュー" },
];

function detectSlashQuery(text: string, cursor: number): string | null {
	if (cursor === 0) return null;
	const before = text.slice(0, cursor);
	const lastNewline = before.lastIndexOf("\n");
	const lastSpace = before.lastIndexOf(" ");
	const anchor = Math.max(lastNewline, lastSpace) + 1;
	const segment = before.slice(anchor);
	if (!segment.startsWith("/")) return null;
	if (segment.includes(" ")) return null;
	return segment;
}

const CHIP_NAME_STYLE: CSSProperties = {
	overflow: "hidden",
	textOverflow: "ellipsis",
	whiteSpace: "nowrap",
};

/** File から作った object URL を返し、ファイルが変わるかアンマウントしたら解放する。 */
function useObjectUrl(file: File): string | null {
	const [url, setUrl] = useState<string | null>(null);
	useEffect(() => {
		const objectUrl = URL.createObjectURL(file);
		setUrl(objectUrl);
		return () => URL.revokeObjectURL(objectUrl);
	}, [file]);
	return url;
}

function ImageAttachmentChip({ file, removeButton }: { file: File; removeButton: ReactNode }) {
	const url = useObjectUrl(file);
	return (
		<span
			data-testid="composer-attachment-chip"
			style={{
				...CHIP_STYLE,
				gap: 6,
				maxWidth: 160,
				padding: "2px 8px 2px 2px",
				color: "var(--fg-2)",
				borderRadius: "var(--radius-4)",
			}}
		>
			<img
				src={url ?? undefined}
				style={{
					width: 36,
					height: 36,
					borderRadius: "var(--radius-2)",
					objectFit: "cover",
					flexShrink: 0,
				}}
			/>
			<span title={file.name} data-tooltip-if-truncated style={CHIP_NAME_STYLE}>
				{file.name}
			</span>
			{removeButton}
		</span>
	);
}

/**
 * 入力欄の添付チップ。画像はサムネイル付き、それ以外は名前だけのチップにする。
 * 送信時に本文へ入るかワークスペースへ置かれるかは、見た目では区別しない。
 */
function AttachmentChip({
	attachment,
	onRemove,
}: {
	attachment: ComposerAttachment;
	onRemove: (id: string) => void;
}) {
	const removeButton = (
		<button
			type="button"
			data-testid="composer-attachment-remove"
			onClick={() => onRemove(attachment.id)}
			style={CHIP_REMOVE_BUTTON_STYLE}
		>
			×
		</button>
	);
	if (attachment.kind === "file" && classifyAttachment(attachment.file) === "image") {
		return <ImageAttachmentChip file={attachment.file} removeButton={removeButton} />;
	}
	const name = attachment.kind === "file" ? attachment.file.name : attachment.uploaded.name;
	return (
		<span
			data-testid="composer-attachment-chip"
			style={{
				...CHIP_STYLE,
				gap: 4,
				maxWidth: 200,
				padding: "2px 8px",
				color: "var(--fg-2)",
				borderRadius: "var(--radius-full)",
			}}
		>
			<Icon name="file" size={14} color="var(--fg-3)" />
			<span title={name} data-tooltip-if-truncated style={CHIP_NAME_STYLE}>
				{name}
			</span>
			{removeButton}
		</span>
	);
}

export function ComposerInput({
	onSend,
	sendDisabled = false,
	inputDisabled = false,
	placeholder,
	sendPending = false,
	status = "idle",
	onStop,
	ref,
}: ComposerInputProps) {
	const chatId = useChatStore((s) => s.chatId);
	const latestContextUsage = useChatStore(selectLatestContextUsage);
	// チャットごとの下書きはストアから初期値だけ取り、以降は内部 state のみ更新する。
	// これによりタイピング中はストア書き込み・親再レンダーが発生しない。
	// 親側で `key={chatId}` により ComposerInput が再マウントされるため、
	// チャット切替時にこの初期化が再度走る。
	const [text, setText] = useState(() =>
		chatId ? (useChatDraftStore.getState().drafts[chatId]?.text ?? "") : "",
	);
	const [attachments, setAttachments] = useState<ComposerAttachment[]>(() =>
		chatId ? (useChatDraftStore.getState().drafts[chatId]?.attachments ?? []) : [],
	);
	const [cursor, setCursor] = useState(0);
	// 送信履歴の閲覧状態。閲覧開始時にスナップショットを historyRef へ取り込み、
	// 閲覧中はそのスナップショットを使う（再レンダーを避けるため messages は購読しない）。
	const [historyNav, setHistoryNav] = useState<HistoryNavState>(INITIAL_HISTORY_NAV);
	const historyRef = useRef<string[]>([]);
	const [paletteIndex, setPaletteIndex] = useState(0);
	// Escape dismissal is per slash-token. When the user edits the token again
	// (slashQuery changes), we clear the flag so the palette can re-open.
	const [paletteDismissed, setPaletteDismissed] = useState(false);
	const textareaRef = useRef<HTMLTextAreaElement>(null);
	const fileInputRef = useRef<HTMLInputElement>(null);
	const textRef = useRef(text);
	textRef.current = text;
	const attachmentsRef = useRef(attachments);
	attachmentsRef.current = attachments;

	// アンマウント時（＝チャット切替時）に最新テキスト・添付をストアへ永続化する。
	useEffect(() => {
		if (!chatId) return;
		return () => {
			useChatDraftStore.getState().setDraftText(chatId, textRef.current);
			useChatDraftStore.getState().setDraftAttachments(chatId, attachmentsRef.current);
		};
	}, [chatId]);

	// テキストを現在のカーソル位置へ差し込む。前後が空白でなければ空白で区切る。
	const insertAtCursor = useCallback((insert: string) => {
		const prev = textRef.current;
		const pos = Math.min(textareaRef.current?.selectionStart ?? prev.length, prev.length);
		const before = prev.slice(0, pos);
		const after = prev.slice(pos);
		const needsLeadingSpace = before.length > 0 && !/\s$/.test(before);
		const needsTrailingSpace = after.length > 0 && !/^\s/.test(after);
		const insertText = `${needsLeadingSpace ? " " : ""}${insert}${needsTrailingSpace ? " " : ""}`;
		const nextCursor = before.length + insertText.length;
		const next = `${before}${insertText}${after}`;
		// 再描画前に続けて差し込まれても前の差し込みを失わないよう、ref も先に進めておく。
		textRef.current = next;
		setText(next);
		setCursor(nextCursor);
		setHistoryNav(INITIAL_HISTORY_NAV);
		requestAnimationFrame(() => {
			const el = textareaRef.current;
			if (!el) return;
			el.focus();
			el.setSelectionRange(nextCursor, nextCursor);
		});
	}, []);

	// 外部（ファイルツリー・プレビュータブ）からの挿入要求を、現在のカーソル位置へ取り込む。
	const pendingInsert = useChatDraftStore((s) => (chatId ? s.pendingInserts[chatId] : undefined));
	useEffect(() => {
		if (!chatId || !pendingInsert) return;
		insertAtCursor(pendingInsert);
		useChatDraftStore.getState().consumeDraftInsert(chatId);
	}, [chatId, pendingInsert, insertAtCursor]);

	const pendingRestore = useChatDraftStore((s) => (chatId ? s.pendingRestores[chatId] : undefined));
	useEffect(() => {
		if (!chatId || pendingRestore === undefined) return;
		const restoredText = pendingRestore.text;
		if (textRef.current.trim() === "") {
			setText(restoredText);
			setCursor(restoredText.length);
			setHistoryNav(INITIAL_HISTORY_NAV);
			requestAnimationFrame(() => {
				const el = textareaRef.current;
				if (!el) return;
				el.focus();
				el.setSelectionRange(restoredText.length, restoredText.length);
			});
		}
		// 送信後に追加された添付は残し、戻した添付をその前に並べる。
		const restoredAttachments = pendingRestore.attachments;
		if (restoredAttachments.length > 0) setAttachments((prev) => [...restoredAttachments, ...prev]);
		useChatDraftStore.getState().consumeDraftRestore(chatId);
	}, [chatId, pendingRestore]);

	const hasContent = text.trim() !== "" || attachments.length > 0;
	const primaryAction = useMemo(
		() =>
			composerPrimaryAction({
				status,
				hasContent,
				sendPending,
				sendDisabled,
			}),
		[status, hasContent, sendPending, sendDisabled],
	);

	// 新規/既存チャットへ切り替わったらテキスト入力にフォーカスし、ユーザーが
	// 即座にタイプ開始できるようにする。入力は送信不可時でも常に可能なため、
	// 読み込み完了を待たずにフォーカスしてよい。
	useEffect(() => {
		textareaRef.current?.focus();
	}, [chatId]);

	// 別のビューから新規チャットを始めたときは、チャットのビューが表示されてからフォーカスする
	// (非表示の間は focus() が効かない)。
	const composerFocusRequested = useLayoutStore((s) => s.composerFocusRequested);
	const chatsViewVisible = useLayoutStore((s) => s.mainView === "chats");
	useEffect(() => {
		if (!composerFocusRequested || !chatsViewVisible) return;
		textareaRef.current?.focus();
		useLayoutStore.getState().clearComposerFocusRequest();
	}, [composerFocusRequested, chatsViewVisible]);

	const slashQuery = useMemo(() => detectSlashQuery(text, cursor), [text, cursor]);

	const visibleCommands = useMemo(() => {
		if (slashQuery === null) return [];
		const q = slashQuery.toLowerCase();
		return SLASH_COMMANDS.filter((cmd) => cmd.slug.toLowerCase().startsWith(q));
	}, [slashQuery]);

	const paletteOpen = !paletteDismissed && visibleCommands.length > 0 && slashQuery !== null;

	useEffect(() => {
		setPaletteIndex(0);
		setPaletteDismissed(false);
	}, [slashQuery]);

	// Auto-resize the textarea on every content change so Shift+Enter newlines
	// are visible instead of being hidden inside a 1-row box. useLayoutEffect
	// avoids a visible flicker between the "auto" reset and the measured height.
	useLayoutEffect(() => {
		const el = textareaRef.current;
		if (!el) return;
		resizeTextarea(el, TEXTAREA_MAX_HEIGHT);
	}, [text]);

	const handleSend = useCallback(() => {
		if (primaryAction.kind !== "send" || primaryAction.blocker !== null) return;
		void onSend(text.trim(), attachments).catch((err: unknown) => {
			console.error("メッセージ送信に失敗しました", err);
		});
		setText("");
		setAttachments([]);
		setCursor(0);
		setHistoryNav(INITIAL_HISTORY_NAV);
	}, [text, attachments, primaryAction, onSend]);

	// コンポーザーへ届いたファイルを取り込む。振り分けは送信時に行い、ここではチップに載せるだけにする。
	const addFiles = useCallback(
		(files: File[]) => {
			// 入力無効中は D&D 経由 (CenterPane からの委譲) も受け付けない。
			if (inputDisabled) return;
			if (files.length === 0) return;
			setAttachments((prev) => [...prev, ...files.map(attachmentFromFile)]);
		},
		[inputDisabled],
	);

	const focus = useCallback(() => textareaRef.current?.focus(), []);

	// CenterPane がチャット履歴+コンポーザー全体でのドロップを受け付け、ここへ委譲する。
	useImperativeHandle(ref, () => ({ addFiles, focus }), [addFiles, focus]);

	const handleRemoveAttachment = useCallback((id: string) => {
		setAttachments((prev) => prev.filter((attachment) => attachment.id !== id));
	}, []);

	const handlePaste = useCallback(
		(e: ReactClipboardEvent<HTMLTextAreaElement>) => {
			// 種別で絞らず addFiles へ渡す。D&D・添付ボタンと同じ扱いにする。
			const files = Array.from(e.clipboardData.items)
				.filter((item) => item.kind === "file")
				.map((item) => item.getAsFile())
				.filter((file): file is File => file !== null);
			if (files.length === 0) return;
			// テキストも同時に貼り付けられている場合は、そちらのネイティブ挙動を残す。
			if (e.clipboardData.getData("text/plain") === "") {
				e.preventDefault();
			}
			addFiles(files);
		},
		[addFiles],
	);

	// 履歴から呼び出したテキストを反映し、カーソルを末尾へ置いてすぐ編集・送信できるようにする。
	const applyRecalledText = useCallback((value: string) => {
		setText(value);
		requestAnimationFrame(() => {
			const el = textareaRef.current;
			if (!el) return;
			el.focus();
			el.setSelectionRange(value.length, value.length);
			setCursor(value.length);
		});
	}, []);

	const insertCommand = useCallback(
		(slug: string) => {
			const before = text.slice(0, cursor);
			const after = text.slice(cursor);
			const lastNewline = before.lastIndexOf("\n");
			const lastSpace = before.lastIndexOf(" ");
			const anchor = Math.max(lastNewline, lastSpace) + 1;
			const head = before.slice(0, anchor);
			const next = `${head}${slug} ${after}`;
			setText(next);
			const nextCursor = head.length + slug.length + 1;
			requestAnimationFrame(() => {
				const el = textareaRef.current;
				if (!el) return;
				el.focus();
				el.setSelectionRange(nextCursor, nextCursor);
				setCursor(nextCursor);
			});
		},
		[text, cursor],
	);

	const handleKeyDown = useCallback(
		(e: ReactKeyboardEvent<HTMLTextAreaElement>) => {
			// かな漢字変換に消費されたキー(確定 Enter を含む)では送信・履歴などの処理を行わない。
			if (isImeConsumedKeyDown(e.nativeEvent)) return;

			// 入力中の Ctrl/Cmd+F はブラウザの検索ではなくチャット履歴の検索を開く。
			// 検索する履歴が無いときは何もせず、既定動作 (ブラウザ検索) に任せる。
			if (e.key === "f" && (e.ctrlKey || e.metaKey) && !e.shiftKey && !e.altKey) {
				if (useChatStore.getState().messages.length === 0) return;
				e.preventDefault();
				useTranscriptSearchStore.getState().openSearch();
				return;
			}

			if (paletteOpen) {
				if (e.key === "ArrowDown") {
					e.preventDefault();
					setPaletteIndex((i) => Math.min(i + 1, visibleCommands.length - 1));
					return;
				}
				if (e.key === "ArrowUp") {
					e.preventDefault();
					setPaletteIndex((i) => Math.max(i - 1, 0));
					return;
				}
				if (e.key === "Enter" || e.key === "Tab") {
					e.preventDefault();
					const cmd = visibleCommands[paletteIndex];
					if (cmd) insertCommand(cmd.slug);
					return;
				}
				if (e.key === "Escape") {
					e.preventDefault();
					setPaletteDismissed(true);
					return;
				}
			}

			// 修飾キーなし・テキスト選択なしの ↑/↓ は、1 段ずつ動いて先頭/末尾へ着いてから送信履歴を辿る。
			const plainArrow = !e.shiftKey && !e.metaKey && !e.ctrlKey && !e.altKey;
			const el = textareaRef.current;
			if (
				el &&
				plainArrow &&
				el.selectionStart === el.selectionEnd &&
				(e.key === "ArrowUp" || e.key === "ArrowDown")
			) {
				const action = resolveComposerArrow(
					e.key,
					el.value,
					el.selectionStart,
					historyNav.index !== null,
				);
				if (action.kind === "native") {
					// 折り返した段も 1 段ずつ動かせるよう既定の移動に任せる。一番上 (下) の段で
					// 動かない環境 (Windows など) では、既定の移動を待ってから先頭 (末尾) へ移す。
					const before = el.selectionStart;
					requestAnimationFrame(() => {
						if (el.selectionStart !== before || el.selectionEnd !== before) return;
						el.setSelectionRange(action.edge, action.edge);
						setCursor(action.edge);
					});
					return;
				}
				if (e.key === "ArrowUp" && historyNav.index === null) {
					historyRef.current = buildUserMessageHistory(useChatStore.getState().messages);
				}
				const result =
					e.key === "ArrowUp"
						? historyPrevious(historyNav, historyRef.current, textRef.current)
						: historyNext(historyNav, historyRef.current);
				if (result) {
					e.preventDefault();
					setHistoryNav(result.state);
					applyRecalledText(result.text);
					return;
				}
			}

			if (e.key === "Enter" && !e.shiftKey) {
				e.preventDefault();
				handleSend();
			}
		},
		[
			paletteOpen,
			visibleCommands,
			paletteIndex,
			insertCommand,
			handleSend,
			historyNav,
			applyRecalledText,
		],
	);

	const handleChange = useCallback((e: ChangeEvent<HTMLTextAreaElement>) => {
		const next = e.target.value;
		setText(next);
		setCursor(e.target.selectionStart ?? next.length);
		// 履歴を編集した時点で閲覧モードを抜け、編集後のテキストを現在の下書きとして扱う。
		setHistoryNav((s) => (s.index === null ? s : INITIAL_HISTORY_NAV));
	}, []);

	const handleSelect = useCallback(() => {
		const el = textareaRef.current;
		if (!el) return;
		setCursor(el.selectionStart ?? el.value.length);
	}, []);

	const handleFileChange = useCallback(
		(e: ChangeEvent<HTMLInputElement>) => {
			const files = Array.from(e.target.files ?? []);
			// 同名ファイルの再選択でも change が発火するよう、取得後すぐに値をリセットする。
			e.target.value = "";
			addFiles(files);
		},
		[addFiles],
	);

	// 送信できる状態では押せること自体が示されているため、ヒントは出さない。入力が空などで
	// 押せない間だけ、次に何をすれば良いかとしてキーボード操作を案内する。
	const sendBlocker = primaryAction.kind === "send" ? primaryAction.blocker : null;
	const sendButtonDisabled = primaryAction.kind !== "send" || sendBlocker !== null;
	const sendButton = (
		<IconButton
			icon="arrowU"
			size={PRIMARY_BUTTON_SIZE}
			onClick={handleSend}
			disabled={sendButtonDisabled}
			data-testid="composer-send"
			// 送信可能なときは primary submit と同じ塗り。無効時は neutral まで落として
			// ボタンの輪郭は残しつつ、押せないことを彩度と文字色で示す。文字は塗りの対である
			// --on-neutral-solid ではなく無効役割の --fg-4 を使う (白のままだと押せそうに見える)。
			color={sendButtonDisabled ? "var(--fg-4)" : "var(--on-accent-solid)"}
			background={sendButtonDisabled ? "var(--neutral-solid)" : "var(--accent-solid)"}
			hoverBackground="var(--accent-solid-hover)"
			style={{
				borderRadius: "var(--radius-full)",
				cursor: sendButtonDisabled ? "not-allowed" : "pointer",
				// 無効時の弱さは背景と文字色で表すため、既定の減光は打ち消す。
				opacity: 1,
				flexShrink: 0,
			}}
		/>
	);

	return (
		<div
			data-testid="composer-input"
			style={{
				position: "relative",
				// 左右の余白は列 (CenterPane) 側だけが持つ。ここで足すと箱がメッセージ本文より
				// 内側へ入ってしまう。
				padding: "10px 0 14px",
			}}
		>
			{/* Row 1 — 添付チップ (添付がある場合のみ表示) */}
			{attachments.length > 0 && (
				<div
					data-testid="composer-chips"
					style={{
						display: "flex",
						flexWrap: "wrap",
						gap: 6,
						marginBottom: 8,
					}}
				>
					{attachments.map((attachment) => (
						<AttachmentChip
							key={attachment.id}
							attachment={attachment}
							onRemove={handleRemoveAttachment}
						/>
					))}
				</div>
			)}

			<div
				style={{
					display: "flex",
					flexDirection: "column",
					background: "var(--surface-1)",
					borderRadius: "var(--radius-4)",
					// 下端は操作列を箱の縁へ寄せる。ボタン自身が内側に余白を持つため、
					// ここを詰めても glyph が縁に触れない。
					padding: "6px 8px 2px",
					boxShadow: "var(--shadow-card), var(--shadow-composer-boundary)",
				}}
			>
				<textarea
					ref={textareaRef}
					value={text}
					disabled={inputDisabled}
					onChange={handleChange}
					onKeyDown={handleKeyDown}
					onSelect={handleSelect}
					onPaste={handlePaste}
					placeholder={
						placeholder ?? (status === "streaming" ? "フォローアップ" : "メッセージを入力…")
					}
					rows={1}
					data-testid="composer-textarea"
					style={{
						width: "100%",
						minHeight: 24,
						maxHeight: TEXTAREA_MAX_HEIGHT,
						overflowY: "auto",
						resize: "none",
						border: 0,
						outline: "none",
						background: "transparent",
						fontFamily: "inherit",
						fontSize: "var(--text-ui)",
						lineHeight: "var(--leading-normal)",
						color: "var(--fg-1)",
						padding: "4px 4px",
					}}
				/>
				<div
					style={{
						display: "flex",
						alignItems: "center",
						gap: 8,
					}}
				>
					<input
						ref={fileInputRef}
						type="file"
						multiple
						onChange={handleFileChange}
						className="hidden"
						data-testid="file-input"
						style={{ display: "none" }}
					/>
					<IconButton
						icon="plus"
						onClick={() => fileInputRef.current?.click()}
						disabled={inputDisabled}
						data-testid="composer-attach"
						title="ファイルを添付"
						data-tooltip-side="top"
						style={{ borderRadius: "var(--radius-full)", flexShrink: 0 }}
					/>
					{/* 以下のセレクタ類は memo 済み。1 文字ごとの再描画に巻き込まない。 */}
					<ChatPermissionModeSelector />
					<span style={{ flex: 1 }} />
					<ChatModelSelector />
					{latestContextUsage && <ContextUsageMeter usage={latestContextUsage} />}
					{primaryAction.kind === "stop" ? (
						<IconButton
							icon="stop"
							iconSize={12}
							size={PRIMARY_BUTTON_SIZE}
							// 同じ位置に出る送信ボタンと同じ塗り。塗りが付いたので輪郭の罫線は要らない。
							color="var(--on-accent-solid)"
							background="var(--accent-solid)"
							hoverBackground="var(--accent-solid-hover)"
							onClick={onStop}
							data-testid="composer-stop"
							title="処理を停止"
							data-tooltip-side="top"
							style={{
								borderRadius: "var(--radius-full)",
								flexShrink: 0,
							}}
						/>
					) : primaryAction.kind === "stopPending" ? (
						<span
							data-testid="composer-stop-pending"
							title="停止しています"
							data-tooltip-side="top"
							// 状態自体が既定の 1 秒より早く終わることが多く、遅延させると案内が出ないまま消える。
							data-tooltip-delay="0"
							// 送信 / 停止と同じ丸を保ち、押せないことは無効時と同じ neutral の塗りで示す。
							style={{
								width: PRIMARY_BUTTON_SIZE,
								height: PRIMARY_BUTTON_SIZE,
								display: "inline-flex",
								alignItems: "center",
								justifyContent: "center",
								borderRadius: "var(--radius-full)",
								background: "var(--neutral-solid)",
								flexShrink: 0,
							}}
						>
							<span
								style={{
									width: 12,
									height: 12,
									borderRadius: "var(--radius-full)",
									// 塗りの上では --line-1 の輪が沈むため、回っている弧だけを見せる。
									// 弧の色は無効時の送信ボタンと同じ --fg-4 に揃える。
									border: "2px solid transparent",
									borderTopColor: "var(--fg-4)",
									animation: "izanagi-spin 0.9s linear infinite",
								}}
							/>
						</span>
					) : (
						// 有効/無効でラップの有無を変えると要素の型が変わって DOM ノードごと作り直され、
						// ポインタを乗せたまま状態が変わったときに mouseenter が来ない (送信直後に
						// ヒントが出ない・ホバー塗りが消える)。常に包み、中身だけ出し分ける。
						<RichTooltip side="top" {...sendHintFor(sendBlocker)}>
							{sendButton}
						</RichTooltip>
					)}
				</div>
			</div>

			{/* Slash palette */}
			{paletteOpen && (
				<ul
					data-testid="slash-palette"
					style={{
						position: "absolute",
						left: 0,
						right: 0,
						bottom: "100%",
						margin: 0,
						padding: 4,
						listStyle: "none",
						background: "var(--surface-1)",
						borderRadius: "var(--radius-4)",
						boxShadow: "var(--shadow-popover)",
						maxHeight: 180,
						overflowY: "auto",
						zIndex: 20,
					}}
				>
					{visibleCommands.map((cmd, i) => (
						<li
							key={cmd.slug}
							data-testid={`slash-option-${cmd.slug.slice(1)}`}
							data-selected={i === paletteIndex ? "true" : "false"}
							onMouseDown={(e) => {
								e.preventDefault();
								insertCommand(cmd.slug);
							}}
							style={{
								display: "flex",
								alignItems: "baseline",
								gap: 8,
								padding: "6px 8px",
								borderRadius: "var(--radius-2)",
								cursor: "pointer",
								background: i === paletteIndex ? "var(--menu-hover-bg)" : "transparent",
								fontSize: "var(--text-caption)",
								color: i === paletteIndex ? "var(--fg-0)" : "var(--fg-1)",
							}}
						>
							<span
								style={{
									fontFamily: "var(--font-mono)",
									color: "var(--accent)",
								}}
							>
								{cmd.slug}
							</span>
							<span style={{ color: "var(--fg-3)", fontSize: "var(--text-caption)" }}>
								{cmd.hint}
							</span>
						</li>
					))}
				</ul>
			)}
		</div>
	);
}
